import { ChevronRight, Sparkles, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useIntegrationsStatusSummary } from "@/hooks/useIntegrationsStatusSummary";
import type { AppPlatform } from "@/types";
import Button from "@/components/ui/Button";
import {
  dismissUpperNotice,
  isWithinSoftDismiss,
  readDismissState,
  upperNoticeKey,
} from "@/lib/upperNoticeDismiss";

/**
 * "Add richer signals" upper-warning card
 * (experience-and-ui.md § 3a "Upper warnings — two surfaces, one look",
 * PO direction Doc A 2026-07-26 / ENG-PO-DOCS-DIRECTIVES-0903 item A7,
 * placement RE-CUT by PO direction chat 2026-09-10).
 *
 * Split out of `ConnectStoreCard.tsx` per PO verbatim: *"ADD RICHER
 * SIGNALS and Connect your store console are two different things. They
 * must be separated only as upper warning but normalize it into how
 * 'Connect your store console' looks."* Same shape as `ConnectStoreCard`
 * (icon + title + description + CTA), the WARNING ramp instead of the
 * CRITICAL ramp.
 *
 * **Single shared mount (A7).** Rendered ONCE from the app shell
 * (`components/layout/Layout.tsx`), above every routed page's
 * `<Outlet />` — NOT a per-page copy. `isHome` is resolved by the shell
 * from `useLocation()`.
 *
 * **PO matrix (chat 2026-09-10, verbatim: _"second one on get reacher
 * signals should be only on home dashboard and if even 1 already
 * connected, it shouldnt be shown"_ + _"and there should be X button to
 * close it"_):**
 *   | Rule                                       | Home dashboard          | All other pages |
 *   |--------------------------------------------|-------------------------|-----------------|
 *   | Add richer signals (no integrations at all) | Showing, dismissable ✕  | Not showing     |
 *   | Add richer signals (≥ 1 API key)            | Not showing             | Not showing     |
 *
 * This SUPERSEDES the 2026-07-26 rows that put the zero-integration card
 * on every page ("Same") and gave the ≥1-key case its own *"Keep going!"*
 * warning. Both are retired: the card is Home-only in EVERY row, and one
 * connected key silences it. Consequence for the count read below — it is
 * now a pure zero/non-zero test, but it stays a full per-connector count
 * (not a short-circuit on the first hook) because under-counting is the
 * exact regression A7's review caught, and a count is what the pin reads.
 *
 * **Integration-count source.** Composes the SAME per-connector status
 * hooks that already back the Settings → Integrations connector cards
 * (the three cross-platform MMP reads `useAppsFlyerStatus` /
 * `useAdjustStatus` / `useBranchStatus`, plus the platform-conditional
 * `useAppleSearchAdsStatus` [iOS] / `useFirebaseStatus` [Android]) rather
 * than adding a count field to `DashboardState` — chosen because those
 * hooks are the single existing owner of "is this Tier-2 key connected"
 * (Settings → Integrations reads the exact same queries, so this card can
 * never drift from what the Integrations page shows), whereas
 * `DashboardState` is owned by `useDashboardState` for the Home-only,
 * per-app timeline payload. Google Ads is intentionally excluded from the
 * count — § 3a item 9 / § Progressive integration tiers list it
 * "(coming soon)", so it is not yet a connectable key.
 *
 * **Dismissal.** `✕` close control, per the same 7-day soft-dismiss +
 * permanent-after-second-skip rule `WorkflowsOnboardingCard` uses
 * (§ 3a item 4), persisted per WORKSPACE in `localStorage` through the shared
 * `lib/upperNoticeDismiss` helper (§ 12 § 3.1 Upper notice; Track C
 * question 4, 2026-10-18 — an app switch must not resurrect it). Icon-only control ⇒
 * carries an `aria-label` per UI_COMPONENT_CONTRACT.md § Accessibility
 * floor item 6.
 *
 * **Semantic ramp.** "Add richer signals" = the WARNING ramp (PO
 * verbatim, § 3a). Large surfaces stay on the pre-2026-08-26 Tailwind
 * `warn-*` ramp per UI_COMPONENT_CONTRACT.md's "Destructive-action +
 * connector surfaces" row — the BRANDING semantic ramp
 * (`--color-warning` etc.) is scoped to Badges, not large card surfaces.
 * This is a STATUS use (evidence-strength state), never a category/tier
 * label, so it does not trip ENG-STATUS-COLOUR-MISUSE.
 */

interface AddRicherSignalsCardProps {
  /** The active app's platform. `null` while unresolved — renders nothing. */
  platform: AppPlatform | null;
  /** The active app id, for the platform-conditional per-app status reads. */
  appId: string | null;
  /** True when the shell is mounted under the `/` Dashboard route. */
  isHome: boolean;
  /** Dismissal persists per WORKSPACE (§ 12 § 3.1), never per app. */
  workspaceId: string | null;
}

export default function AddRicherSignalsCard({
  platform,
  appId,
  isHome,
  workspaceId,
}: AddRicherSignalsCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Every Tier-2 connector counts, not just AppsFlyer: Adjust and Branch are
  // live MMP connectors the Integrations page renders as its peers under
  // "Attribution & analytics". Omitting them told a workspace running Adjust
  // to "add richer signals" it had already added -- the same second-class-MMP
  // class ENG-MMP-REVENUE-PATH-FANOUT tracks on the revenue path. All of
  // them arrive in the ONE integrations-status summary the shell reads
  // (ENG-APP-UX-TUNEUP floor row 11), never as per-connector probes.
  const summary = useIntegrationsStatusSummary(appId);

  // Re-render trigger only. The dismissal itself is re-read from
  // localStorage on EVERY render rather than seeded into `useState`,
  // because this card lives in the app shell and stays MOUNTED across app
  // switches -- a lazy `useState` initializer would freeze the FIRST app's
  // answer and then show/hide the card against the wrong app's dismissal.
  const [, bumpDismissTick] = useState(0);

  // Matrix: Home-only in EVERY row (PO chat 2026-09-10). Gated ahead of the
  // count branch so no row can leak onto another page.
  if (!isHome) return null;

  if (!platform || !appId || !workspaceId) return null;

  // Per-workspace, re-read on every render (shared helper, § 12 § 3.1).
  const dismissKey = upperNoticeKey("add-richer-signals", workspaceId);
  if (isWithinSoftDismiss(readDismissState(dismissKey))) return null;

  // Avoid a connect-then-recount flash: while the read is still resolving,
  // render nothing rather than a count that is about to change.
  if (summary.isLoading) return null;

  const platformStatus =
    platform === "ios"
      ? summary.data?.apple_search_ads
      : summary.data?.firebase;

  const connectedCount = [
    summary.data?.appsflyer?.connected,
    summary.data?.adjust?.connected,
    summary.data?.branch?.connected,
    platformStatus?.connected,
  ].filter(Boolean).length;

  // Matrix row ">= 1 API key": one connected key silences the card
  // everywhere, Home included (PO chat 2026-09-10). The old ">= 2" row and
  // the Home-only "Keep going!" warning it gated are retired.
  if (connectedCount >= 1) return null;

  const handleDismiss = () => {
    dismissUpperNotice(dismissKey);
    bumpDismissTick((tick) => tick + 1);
  };

  // Matrix row "no integrations at all": Home only, re-based into the
  // Connect-your-store-console look, dismissable.
  return (
    <section
      aria-labelledby="add-richer-signals-heading"
      className="border-warn-soft bg-warn-bg relative rounded-2xl border p-5 shadow-sm"
    >
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={t("dashboard.addRicherSignalsCard.dismissAriaLabel")}
        className="text-muted hover:text-ink-soft absolute right-3 top-3 rounded-md p-1"
      >
        <X size={16} aria-hidden="true" />
      </button>
      <div className="flex items-start gap-3 pr-8">
        <Sparkles className="text-warn mt-0.5 h-5 w-5" aria-hidden="true" />
        <div className="flex-1">
          <h2
            id="add-richer-signals-heading"
            className="text-ink text-sm font-semibold"
          >
            {t("dashboard.addRicherSignalsCard.title")}
          </h2>
          <p className="text-ink-soft mt-1 text-sm leading-relaxed">
            {t("dashboard.addRicherSignalsCard.description")}
          </p>
          <Button
            size="compact"
            onClick={() => navigate("/app/integrations")}
            className="mt-3"
          >
            {t("dashboard.addRicherSignalsCard.connectButton")}
            <ChevronRight size={14} aria-hidden="true" />
          </Button>
        </div>
      </div>
    </section>
  );
}
